import React from "react";
import { WizardData } from "./OfferWizard";
import {
  DocumentIcon,
  UserIcon,
  EmailIcon,
  PhoneIcon,
  CalendarIcon,
  ClockIcon,
  BedIcon,
  UsersIcon,
} from "../icons";

interface BasicInfoSectionProps {
  data: WizardData;
  mode?: "wizard" | "public";
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  data,
  mode = "wizard",
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const calculateNights = () => {
    if (!data.checkInDate || !data.checkOutDate) return 0;
    const checkIn = new Date(data.checkInDate);
    const checkOut = new Date(data.checkOutDate);
    const diffTime = Math.abs(checkOut.getTime() - checkIn.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="mb-8" data-section="basic-info">
      {/* Professional Header */}
      <div className="border-b-2 border-slate-800 pb-2 mb-6">
        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
          <DocumentIcon className="w-7 h-7 text-slate-600" />
          Szczegóły Oferty
        </h2>
        <p className="text-slate-600 mt-1 text-sm">
          Podstawowe informacje o Państwa pobycie
        </p>
      </div>

      {/* Main Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Offer Title */}
          <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <DocumentIcon className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-700 mb-1">
                Tytuł oferty
              </div>
              <div className="text-base font-semibold text-slate-900 break-words">
                {data.title}
              </div>
            </div>
          </div>

          {/* Client Info */}
          <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <UserIcon className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-700 mb-1">
                Klient
              </div>
              <div className="text-base font-semibold text-slate-900">
                {data.clientName}
              </div>
            </div>
          </div>

          {/* Contact Info - only in wizard mode */}
          {mode === "wizard" && (
            <>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <EmailIcon className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-700 mb-1">
                    Email
                  </div>
                  <div className="text-base text-slate-900 break-all">
                    {data.clientEmail}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <PhoneIcon className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-700 mb-1">
                    Telefon
                  </div>
                  <div className="text-base text-slate-900">
                    {data.clientPhone || "Nie podano"}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Dates */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <CalendarIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-blue-700 mb-1">
                Termin pobytu
              </div>
              <div className="text-base font-semibold text-blue-900">
                {formatDate(data.checkInDate)} - {formatDate(data.checkOutDate)}
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <ClockIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-blue-700 mb-1">
                Długość pobytu
              </div>
              <div className="text-base font-semibold text-blue-900">
                {calculateNights()} {calculateNights() === 1 ? "noc" : "noce"}
              </div>
            </div>
          </div>

          {/* Room & Guest Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <BedIcon className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-emerald-700 mb-1">
                  Pokoje
                </div>
                <div className="text-sm font-semibold text-emerald-900">
                  {data.numberOfRooms || Math.ceil(data.numberOfGuests / 2)}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <UsersIcon className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-emerald-700 mb-1">
                  Osoby
                </div>
                <div className="text-sm font-semibold text-emerald-900">
                  {data.numberOfGuests}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description Section */}
      {data.description && (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="text-sm font-medium text-amber-800 mb-2">
            Jak zrozumieliśmy Państwa sytuację:
          </div>
          <div className="text-base text-amber-900 leading-relaxed">
            {data.description}
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicInfoSection;
